import './styles.css'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box, Circle, Flex, Spacer, Square } from '@chakra-ui/react'
import { DeleteXIcon } from '@editor/assets/icons'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { LineConfig, LineStyle, TextConfig } from '@editor/interfaces/canvas'
import { TextAnnotationProps } from '@editor/interfaces/canvas/textAnnotation'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { RootState, useAppDispatch } from '@editor/store/app'
import {
  setAdjustingElementId,
  setDisableCreate,
  setEditingElementId,
  setHoveredElementId,
  toggleSelectedElementIds,
} from '@editor/store/tool'
import { StylesProvider, Tooltip } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { assign, clamp } from 'lodash'
import { useSelector } from 'react-redux'
import { Group, Matrix4, Quaternion, Vector2, Vector3 } from 'three'
import { Line2, LineGeometry } from 'three-stdlib'
import { useThrottledCallback } from 'use-debounce'

import { DEFAULT_LINE_MATERIAL } from '../../../editor/objects/Polyline/constants'
import { hasPreventExitEditingAncestor } from '../../utils'
import Textbox, { TextboxRef } from './Textbox'
import {
  DEFAULT_LINE_CONFIG,
  DEFAULT_TEXT_CONFIG,
  DEFAULT_WIDTH,
  LeadLineAnchors,
  MAX_WIDTH,
  MIN_WIDTH,
} from './definitions'

/**
 * Text annotation component that displays two text, top and bottom separated by a line.
 * On each end of the line, there are lead anchors that can be pulled out to any position in the 3D space.
 *
 * The visual aspect is a mixed of 2D and 3D.
 * - The text and its layout is 2D, rendered in HTML.
 * - The lines (separator and lead lines) are 3D, rendered in Three.js.
 * - The lead anchors are 2D, HTML elements.
 *   - This was done in 2D instead of using CircleAnchor due to HTML having higher priority in the DOM
 *     and thus won't allow hover interactions with 3D elements.
 */
const TextAnnotation = ({
  id,
  transformation,
  leadAnchors = [],
  topText,
  bottomText,
  line,
  text,
  width = DEFAULT_WIDTH,
  editable = false,
  visible,
  onAdjustmentStart,
  onUpdate,
  onUpdated,
  onRemoveLead,
}: TextAnnotationProps) => {
  // Hooks
  const { camera, gl } = useThree()
  const { projectionPlane, raycastToPlane } = useProjectionPlane(camera, gl)
  const { sceneRef, orthographicCamera, checkPreventExitEditingMode, resetPreventExitEditingMode } =
    useBlueprintEditorContext()
  const { calculateDashGapSizes } = usePrintScale()

  // Store
  const dispatch = useAppDispatch()
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const hoveredElementId = useSelector((state: RootState) => state.toolUtil.core.hoveredElementId)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)
  const currentTextColor = useSelector((state: RootState) => state.toolUtil.core.currentTextColor)

  // State
  const [workingLeadId, setWorkingLeadId] = useState<string>('')
  const [leadLineObjects, setLeadLineObjects] = useState<LeadLineAnchors[]>([])

  // Refs
  const windowRef = useRef<Window>(window)
  const groupRef = useRef<Group>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const topBoxRef = useRef<TextboxRef>(null)
  const bottomBoxRef = useRef<TextboxRef>(null)
  const leadPosLeftRef = useRef<HTMLDivElement>(null)
  const leadPosRightRef = useRef<HTMLDivElement>(null)
  const intersectionResultLeft = useRef(new Vector3())
  const intersectionResultRight = useRef(new Vector3())
  const intersectionResultResize = useRef(new Vector3())
  const movingOffset = useRef<Vector3 | null>(null)
  const ptTemp = useRef(new Vector3())
  const resizeHandleRef = useRef<{
    handle: 'topleft' | 'topright' | 'bottomleft' | 'bottomright'
    position: Vector2
    originalX: number
    newX: Vector3
    isMoved?: boolean
  } | null>(null)

  // Derived state
  const isSelected = selectedElementIds.includes(id)
  const isHovered = hoveredElementId === id
  const isHoverSelected = isHovered || isSelected
  const isEditing = editingElementId === id
  const isInteractable = editable

  // Vars
  const { position, matrix } = useMemo(() => {
    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(transformation).decompose(pos, quat, new Vector3())
    return { position: pos, matrix: mtx }
  }, [transformation])
  const actualPosition = useMemo(() => {
    if (movingOffset.current && currentAnchorPosition) {
      return new Vector3().fromArray([
        currentAnchorPosition[0] + movingOffset.current.x,
        currentAnchorPosition[1] + movingOffset.current.y,
        currentAnchorPosition[2] + movingOffset.current.z,
      ])
    }

    return position
  }, [movingOffset, currentAnchorPosition, position])

  // Merge provided text config with defaults.
  // Use refs here instead of useMemo to prevent unnecessary re-renders.
  // If and when the config changes, it will be applied separately.
  const textConfig = useMemo<TextConfig>(() => assign({}, DEFAULT_TEXT_CONFIG, text), [text])
  const lineConfig = useMemo<LineConfig>(() => assign({}, DEFAULT_LINE_CONFIG, line), [line])

  /**
   * Get current line thickness for text annotations.
   * Uses the line prop directly since thickness is already calculated in useMainCanvas.
   */
  const currentLineThickness = line?.thickness || 1

  const makeLineObject = useCallback(() => {
    if (!lineConfig.color) return null

    const geometry = new LineGeometry()
    geometry.setPositions([0, 0, 0, 0, 0, 0]) // Prep with 2 points. Amount of points can't be changed later so we need to set it now.

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = lineConfig.style === LineStyle.Dashed
    outlineMat.color.set(lineConfig.color) // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = currentLineThickness

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()
    outlineLn.scale.set(1, 1, 1)

    return outlineLn
  }, [lineConfig, currentLineThickness])

  /**
   * Separator line
   */
  const separatorLineObject = useMemo(() => makeLineObject(), [makeLineObject])

  /**
   * Lead lines that connects the ends of the separator line to whatever position the user wishes.
   * There can be multiple lead lines stemming of any ends of the separator line.
   */
  useEffect(() => {
    setLeadLineObjects((prev) =>
      leadAnchors.map<LeadLineAnchors>((lead) => {
        const existing = prev.find((l) => l.id === lead.id)
        const object = existing?.object || makeLineObject()

        // Create a new lead line object
        if (!object) {
          return { ...lead, midpoint: existing?.midpoint || new Vector3(), object: null }
        }

        // Set the initial position of the lead line to the anchor point
        if (!existing) {
          object.geometry.setPositions([0, 0, 0, lead.point[0], lead.point[1], lead.point[2]])
          object.computeLineDistances()
        }

        return { ...lead, midpoint: existing?.midpoint || new Vector3(), object }
      }),
    )
  }, [leadAnchors, makeLineObject])

  /**
   * Update the lead lines based on the position of the lead anchors.
   * This is throttled to avoid performance issues.
   */
  const handleUpdateLeads = useThrottledCallback(
    () => {
      if (!leadPosLeftRef.current || !leadPosRightRef.current || !projectionPlane || !separatorLineObject || !visible)
        return

      // --- Update text annotation box (size, scale, etc)
      // Scale based on zoom
      const transforms = [`scale(${orthographicCamera.current.zoom})`]

      // Update the width of the text annotation box
      if (boxRef.current && resizeHandleRef.current) {
        const originalTranslate = boxRef.current.style.transform.match(/translateX\(([^)]+)\)/)
        const rect = boxRef.current.getBoundingClientRect()
        const mouse = resizeHandleRef.current.position
        const oldWidth = boxRef.current.style.width.replace('px', '')

        // Add translateX if it exists
        if (originalTranslate) {
          transforms[1] = `translateX(${originalTranslate[1]})`
        }

        if (resizeHandleRef.current.handle === 'topright' || resizeHandleRef.current.handle === 'bottomright') {
          const newWidth = clamp((mouse.x - rect.x) / orthographicCamera.current.zoom, MIN_WIDTH, MAX_WIDTH)
          boxRef.current.style.setProperty('width', `${newWidth}px`)
        } else {
          const newPosition = raycastToPlane({ x: mouse.x, y: rect.y }, intersectionResultResize.current)
          if (newPosition) {
            const newWidth = Math.round(
              clamp((rect.right - mouse.x) / orthographicCamera.current.zoom, MIN_WIDTH, MAX_WIDTH),
            )

            if (newWidth > MIN_WIDTH) {
              transforms[1] = `translateX(${Math.round((mouse.x - resizeHandleRef.current.originalX) / orthographicCamera.current.zoom)}px)`
              resizeHandleRef.current.newX.copy(newPosition)
              boxRef.current.style.setProperty('width', `${newWidth}px`)
            } else {
              boxRef.current.style.setProperty('width', `${oldWidth}px`)
            }
          }
        }
      }

      boxRef.current?.style.setProperty('transform', transforms.join(' '))

      const left = leadPosLeftRef.current.getBoundingClientRect()
      const right = leadPosRightRef.current.getBoundingClientRect()

      const leftResult = raycastToPlane(
        { x: left.x + left.width / 2, y: left.y + left.height / 2 },
        intersectionResultLeft.current,
      )
      const rightResult = raycastToPlane(
        { x: right.x + right.width / 2, y: right.y + right.height / 2 },
        intersectionResultRight.current,
      )
      if (!rightResult || !leftResult) return

      // --- Update separator line position
      separatorLineObject.geometry.setPositions([
        leftResult.x || 0,
        leftResult.y || 0,
        leftResult.z || 0,
        rightResult.x || 0,
        rightResult.y || 0,
        rightResult.z || 0,
      ])
      separatorLineObject.computeLineDistances()

      // Always update line thickness based on current scale factor
      separatorLineObject.material.linewidth = currentLineThickness

      if (isSelected || isEditing) {
        separatorLineObject.material.dashed = lineConfig.style === LineStyle.Dashed

        const color = currentStrokeColor || lineConfig.color
        if (color) {
          separatorLineObject.material.color.set(color)
        }
      }

      // --- Update lead lines positions
      leadLineObjects.forEach((leadLine) => {
        const { id: leadId, point, object } = leadLine
        if (!object) return

        // If the point is being edited, use the current anchor position
        const pt = workingLeadId === leadId && currentAnchorPosition ? currentAnchorPosition : point

        // Figure out the position of the lead line based on the left and right results
        // Choose the closest end to the lead position
        ptTemp.current.fromArray(pt) // .sub(closest)
        const leftDist = ptTemp.current.distanceTo(leftResult)
        const rightDist = ptTemp.current.distanceTo(rightResult)
        const closest = leftDist < rightDist ? leftResult : rightResult

        // Calculate the midpoint of the lead line
        leadLine.midpoint.copy(ptTemp.current).add(closest).multiplyScalar(0.5)
        sceneRef.current?.getObjectByName(`remove-anchor-${leadId}`)?.position.copy(leadLine.midpoint)

        ptTemp.current.sub(closest)
        object.position.set(closest.x, closest.y, closest.z)
        object.geometry.setPositions([0, 0, 0, ptTemp.current.x, ptTemp.current.y, ptTemp.current.z])
        object.computeLineDistances()

        // Always update line thickness based on current scale factor
        object.material.linewidth = currentLineThickness

        if (isSelected || isEditing) {
          object.material.dashed = lineConfig.style === LineStyle.Dashed

          const color = currentStrokeColor || lineConfig.color
          if (color) {
            object.material.color.set(color)
          }
        }
      })
    },
    1000 / (workingLeadId || movingOffset.current || resizeHandleRef.current ? 60 : 15),
  )

  /**
   * Update line thickness immediately when scale changes
   */
  useEffect(() => {
    if (!separatorLineObject || !visible) return

    // Update separator line thickness
    separatorLineObject.material.linewidth = currentLineThickness

    // Update lead lines thickness
    leadLineObjects.forEach((leadLine) => {
      const { object } = leadLine
      if (object) {
        object.material.linewidth = currentLineThickness
      }
    })
  }, [currentLineThickness, separatorLineObject, leadLineObjects, visible])

  /**
   * On change of print scale, update dash and gap.
   */
  useEffect(() => {
    if (lineConfig.style !== LineStyle.Dashed) return

    const { dashSize, gapSize } = calculateDashGapSizes()
    if (separatorLineObject) {
      separatorLineObject.material.dashSize = dashSize
      separatorLineObject.material.gapSize = gapSize
    }
    leadLineObjects.forEach((leadLine) => {
      const { object } = leadLine
      if (object) {
        object.material.dashSize = dashSize
        object.material.gapSize = gapSize
      }
    })
  }, [lineConfig, separatorLineObject, leadLineObjects, calculateDashGapSizes])

  useFrame(() => {
    if (!visible) return
    handleUpdateLeads()
  })

  /**
   * Handles outside click to exit editing mode.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!visible) return
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        if (event.button === 0) {
          const isSelfPreventExit = hasPreventExitEditingAncestor(event.target as HTMLElement)
          if (isEditing && !checkPreventExitEditingMode() && !isSelfPreventExit) {
            event.preventDefault()
            event.stopPropagation()
            dispatch(setEditingElementId(undefined))
            onUpdated?.({
              id,
              topText: topBoxRef.current?.getText(),
              bottomText: bottomBoxRef.current?.getText(),
              leadAnchors,
            })
            resetPreventExitEditingMode()
          }
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [
    boxRef,
    visible,
    isEditing,
    checkPreventExitEditingMode,
    dispatch,
    onUpdated,
    leadAnchors,
    resetPreventExitEditingMode,
  ])

  /**
   * Mouse up needs to be handled on window level to ensure it works even when
   * the mouse is outside annotation box, or even worse, outside the canvas.
   */
  useEffect(() => {
    const handleMouseUp = () => {
      if (!isInteractable) return

      // Moving annotation
      if (movingOffset.current && currentAnchorPosition) {
        dispatch(setDisableCreate(false))

        // update the position of the text annotation
        matrix.elements[12] = currentAnchorPosition[0] + movingOffset.current.x
        matrix.elements[13] = currentAnchorPosition[1] + movingOffset.current.y
        matrix.elements[14] = currentAnchorPosition[2] + movingOffset.current.z
        onUpdated?.({
          id,
          transformation: matrix.toArray(),
        })
        movingOffset.current = null
        return
      }

      // Modifying lead anchors
      if (workingLeadId) {
        const leadId = workingLeadId
        setWorkingLeadId('')

        if (!leadId || !currentAnchorPosition) return

        onUpdated?.({
          id,
          leadAnchors: [
            {
              id: leadId,
              point: currentAnchorPosition,
            },
          ],
        })
      }

      // Resizing the text annotation
      if (resizeHandleRef.current && boxRef.current) {
        if (resizeHandleRef.current.isMoved) {
          const rect = boxRef.current.getBoundingClientRect()
          const newWidth = clamp(rect.width, MIN_WIDTH, MAX_WIDTH)

          // update the position of the text annotation
          if (resizeHandleRef.current.handle === 'topleft' || resizeHandleRef.current.handle === 'bottomleft') {
            matrix.elements[12] = resizeHandleRef.current.newX.x
            matrix.elements[13] = resizeHandleRef.current.newX.y
            matrix.elements[14] = resizeHandleRef.current.newX.z

            // apply immediately to prevent flickering
            if (groupRef.current) {
              groupRef.current.position.set(
                resizeHandleRef.current.newX.x,
                resizeHandleRef.current.newX.y,
                resizeHandleRef.current.newX.z,
              )
            }
          }

          // Update the text annotation with the new width
          onUpdated?.({
            id,
            transformation: matrix.toArray(),
            width: newWidth,
          })
        }

        resizeHandleRef.current = null

        // we're not setting scale here, we're removing the translation
        boxRef.current?.style.setProperty('transform', `scale(${orthographicCamera.current.zoom})`)
      }
    }

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeHandleRef.current) return
      resizeHandleRef.current.position.set(ev.clientX, ev.clientY)
      resizeHandleRef.current.isMoved = true
    }

    const win = windowRef.current
    win.addEventListener('mouseup', handleMouseUp)
    win.addEventListener('mousemove', handleMouseMove)
    return () => {
      win.removeEventListener('mouseup', handleMouseUp)
      win.removeEventListener('mousemove', handleMouseMove)
    }
  }, [
    id,
    isInteractable,
    workingLeadId,
    currentAnchorPosition,
    matrix,
    visible,
    orthographicCamera,
    onUpdated,
    raycastToPlane,
    dispatch,
  ])

  return (
    <group visible={visible}>
      {/* Separator line */}
      {separatorLineObject && <primitive object={separatorLineObject} renderOrder={1} />}

      {/* Lead lines */}
      {leadLineObjects.map(
        ({ id: leadId, point, midpoint, object, inspectionItemId }) =>
          object && (
            <group key={leadId} renderOrder={1} position={object.position}>
              <primitive name={`lead-line-${id}:${leadId}`} object={object} renderOrder={1} />

              {/* Remove anchor. Using group so we can control it outside of React */}
              {editable && (
                <group name={`remove-anchor-${leadId}`} position={midpoint}>
                  <Html zIndexRange={[1, 5]} renderOrder={1}>
                    <StylesProvider>
                      <Tooltip
                        showArrow
                        positioning={{ placement: 'top' }}
                        label={inspectionItemId ? '検査項目との連携を解除' : undefined}
                        /* TODO: style migration needed - p=8px */
                        /* TODO: style migration needed - fontWeight=normal */
                        /* className="canvas-tooltip" */
                      >
                        <Circle
                          className={[
                            'ta-lead-connector',
                            'remove',
                            isHoverSelected ? 'is-hover-selected' : '',
                            editable ? 'editable' : '',
                          ].join(' ')}
                          size="18px"
                          transform="translateX(-50%) translateY(-50%)"
                          onMouseEnter={() => {
                            dispatch(setHoveredElementId(id))
                          }}
                          onMouseLeave={() => {
                            dispatch(setHoveredElementId(''))
                          }}
                          onMouseDown={
                            onRemoveLead
                              ? (ev) => {
                                  onRemoveLead(ev, id, leadId)
                                  onAdjustmentStart?.()
                                }
                              : undefined
                          }
                        >
                          <Circle size="8px" bg="red" />
                          <DeleteXIcon />
                        </Circle>
                      </Tooltip>
                    </StylesProvider>
                  </Html>
                </group>
              )}

              {/* Lead anchor */}
              {editable && (
                <Html
                  position={workingLeadId === leadId ? currentAnchorPosition : point}
                  zIndexRange={[1, 5]}
                  renderOrder={1}
                >
                  <StylesProvider>
                    <Circle
                      className={[
                        'ta-lead-connector',
                        isHoverSelected ? 'is-hover-selected' : '',
                        workingLeadId === leadId ? 'is-working' : '',
                        editable ? 'editable' : '',
                      ].join(' ')}
                      size="18px"
                      transform="translateX(-50%) translateY(-50%)"
                      onMouseEnter={() => {
                        dispatch(setHoveredElementId(id))
                      }}
                      onMouseLeave={() => {
                        dispatch(setHoveredElementId(''))
                      }}
                      onMouseDown={(ev) => {
                        ev.preventDefault()
                        ev.stopPropagation()
                        setWorkingLeadId(leadId)
                        onUpdate?.(id, leadId)
                        onAdjustmentStart?.()
                      }}
                    >
                      <Circle size="8px" />
                    </Circle>
                  </StylesProvider>
                </Html>
              )}
            </group>
          ),
      )}

      <group ref={groupRef} position={actualPosition}>
        <Html zIndexRange={[1, 9]} renderOrder={1}>
          <StylesProvider>
            <Box
              ref={boxRef}
              className={[
                'ta-container',
                isEditing ? 'is-editing' : '',
                isHoverSelected ? 'is-hover-selected' : '',
                isInteractable ? 'is-interactable' : '',
                visible ? '' : 'is-hidden',
              ].join(' ')}
              style={{
                fontSize: `${textConfig?.size}px`,
                color: isSelected || isEditing ? currentTextColor || textConfig?.color : textConfig?.color,
                width: Math.max(width, MIN_WIDTH),
              }}
              onMouseEnter={() => {
                if (!isInteractable) return
                dispatch(setHoveredElementId(id))
              }}
              onMouseLeave={() => {
                if (!isInteractable) return
                dispatch(setHoveredElementId(''))
              }}
              onMouseDown={(ev) => {
                if (isEditing || !isInteractable) return

                ev.stopPropagation()
                ev.preventDefault()

                const mouseStartPosition =
                  raycastToPlane(
                    {
                      x: ev.clientX,
                      y: ev.clientY,
                    },
                    new Vector3(),
                  ) || position.clone()

                movingOffset.current = position.clone().sub(mouseStartPosition)
                onAdjustmentStart?.()
                onUpdate?.(id)
                dispatch(setDisableCreate(true))
              }}
              onDoubleClick={(ev) => {
                if (!isInteractable) return

                ev.preventDefault()
                ev.stopPropagation()
                dispatch(setEditingElementId(id))
                movingOffset.current = null
              }}
              onClick={(ev) => {
                if (!isInteractable) return

                ev.preventDefault()
                ev.stopPropagation()
                movingOffset.current = null
                if (!isEditing) {
                  const ids = [id]

                  // if it belongs to an inspection item, select that too
                  const inspectionItemIds = leadAnchors.map((la) => la.inspectionItemId).filter(Boolean) as string[]
                  if (inspectionItemIds.length > 0) {
                    ids.push(...inspectionItemIds)
                  }

                  // select this annotation
                  dispatch(toggleSelectedElementIds({ ids, selected: true }))
                }
              }}
            >
              {/* Top Text */}
              <Textbox ref={topBoxRef} isEditing={isEditing} text={topText} focusOnEdit />

              {/* Separator and lead anchors */}
              <Flex h="10px">
                {/* Lead anchor left */}
                <Circle
                  className={['ta-lead-connector', editable ? 'editable' : ''].join(' ')}
                  data-text-annotation-lead-left={id}
                  size="18px"
                  ref={leadPosLeftRef}
                  onMouseDown={(ev) => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    const newId = crypto.randomUUID()
                    onUpdated?.({
                      id,
                      leadAnchors: [
                        {
                          id: newId,
                          point: [0, 0, 0],
                        },
                      ],
                    })
                    setWorkingLeadId(newId)
                    onUpdate?.(id, newId)
                    onAdjustmentStart?.()
                  }}
                >
                  <Circle size="8px" />
                </Circle>

                <Spacer flex={1} />

                {/* Lead anchor right */}
                <Circle
                  className={['ta-lead-connector', editable ? 'editable' : ''].join(' ')}
                  size="18px"
                  ref={leadPosRightRef}
                  onMouseDown={(ev) => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    const newId = crypto.randomUUID()
                    onUpdated?.({
                      id,
                      leadAnchors: [
                        {
                          id: newId,
                          point: [0, 0, 0],
                        },
                      ],
                    })
                    setWorkingLeadId(newId)
                    onUpdate?.(id, newId)
                    onAdjustmentStart?.()
                  }}
                >
                  <Circle size="8px" />
                </Circle>
              </Flex>

              {/* Bottom Text */}
              <Textbox ref={bottomBoxRef} isEditing={isEditing} text={bottomText} />

              {/* Resize Handle */}
              {editable && (
                <Box className="ta-resize-handle">
                  <Square
                    className="top-left"
                    onMouseDown={(ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                      resizeHandleRef.current = {
                        handle: 'topleft',
                        position: new Vector2(ev.clientX, ev.clientY),
                        originalX: boxRef.current?.getBoundingClientRect().x || 0,
                        newX: new Vector3(),
                      }
                      onAdjustmentStart?.()
                      dispatch(setAdjustingElementId(id))
                    }}
                  />
                  <Square
                    className="top-right"
                    onMouseDown={(ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                      resizeHandleRef.current = {
                        handle: 'topright',
                        position: new Vector2(ev.clientX, ev.clientY),
                        originalX: boxRef.current?.getBoundingClientRect().x || 0,
                        newX: new Vector3(),
                      }
                      onAdjustmentStart?.()
                      dispatch(setAdjustingElementId(id))
                    }}
                  />
                  <Square
                    className="bottom-left"
                    onMouseDown={(ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                      resizeHandleRef.current = {
                        handle: 'bottomleft',
                        position: new Vector2(ev.clientX, ev.clientY),
                        originalX: boxRef.current?.getBoundingClientRect().x || 0,
                        newX: new Vector3(),
                      }
                      onAdjustmentStart?.()
                      dispatch(setAdjustingElementId(id))
                    }}
                  />
                  <Square
                    className="bottom-right"
                    onMouseDown={(ev) => {
                      ev.preventDefault()
                      ev.stopPropagation()
                      resizeHandleRef.current = {
                        handle: 'bottomright',
                        position: new Vector2(ev.clientX, ev.clientY),
                        originalX: boxRef.current?.getBoundingClientRect().x || 0,
                        newX: new Vector3(),
                      }
                      onAdjustmentStart?.()
                      dispatch(setAdjustingElementId(id))
                    }}
                  />
                </Box>
              )}
            </Box>
          </StylesProvider>
        </Html>
      </group>
    </group>
  )
}

export default TextAnnotation
